import { ExternalLink } from '@digital-twins/design-system/icons'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { Text } from '@/components/ui/Text'
import { cn } from '@/lib/utils'
import { CONTACT_SALES_URL } from '@/pages/SettingsPage/components/billing/planConfig'

export type SocialCreditsCtaVariant = 'outline' | 'ghost' | 'inline'

interface SocialCreditsCtaProps {
  /** Owners get "Contact sales"; members get "Ask your workspace owner to add credits." */
  isOrgOwner: boolean
  /** Prefix: `<testId>-contact-sales` (owner) or `<testId>-ask-owner` (member). */
  testId: string
  /** `outline` (default) and `ghost` are small buttons; `inline` is a text button for one-line notices. */
  variant?: SocialCreditsCtaVariant
  /** False renders nothing for members (the surrounding copy already asks the owner). */
  showMemberLine?: boolean
  /** Overrides the sales link (defaults to CONTACT_SALES_URL). */
  contactSalesUrl?: string
  className?: string
}

/**
 * The one social credits action (C7). No self-serve purchase yet: owners
 * open the sales form in a new tab, members are told to ask their owner (no
 * button). Every social credit surface (wizard funds row, Topic banner, low
 * line, toasts) uses this.
 */
export function SocialCreditsCta(props: SocialCreditsCtaProps) {
  const {
    isOrgOwner,
    testId,
    variant = 'outline',
    showMemberLine = true,
    contactSalesUrl,
    className,
  } = props

  const { t } = useTranslation('app')

  const onContactSales = () => {
    window.open(contactSalesUrl ?? CONTACT_SALES_URL, '_blank', 'noopener,noreferrer')
  }

  if (!isOrgOwner) {
    if (!showMemberLine) {
      return null
    }
    return (
      <Text
        as="span"
        size="sm"
        color="muted"
        className={cn('leading-5', className)}
        data-testid={`${testId}-ask-owner`}
      >
        {t('socialListening.credits.askOwner')}
      </Text>
    )
  }

  if (variant === 'inline') {
    return (
      <button
        type="button"
        className={cn(
          'inline-flex items-center gap-1 text-[13px] font-medium text-foreground underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm',
          className,
        )}
        onClick={onContactSales}
        data-testid={`${testId}-contact-sales`}
      >
        {t('billing.contactSales')}
        <ExternalLink className="h-3 w-3" aria-hidden />
      </button>
    )
  }

  return (
    <Button
      type="button"
      size="sm"
      variant={variant}
      className={cn('gap-1.5', className)}
      onClick={onContactSales}
      data-testid={`${testId}-contact-sales`}
    >
      {t('billing.contactSales')}
      <ExternalLink className="h-3.5 w-3.5" aria-hidden />
    </Button>
  )
}
